"use client";

import React, { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Globe,
  Server,
  Layers,
  Box,
  Hammer,
  Database,
  HardDrive,
  Clock,
  Terminal,
  ShieldCheck,
  Settings,
  Flame,
  Boxes,
  Users,
  Package as PackageIcon,
  Network,
  Mail,
  ArrowLeftRight,
  ShieldAlert,
  Sparkles,
  KeyRound,
  Cloud,
  Bug,
  Check,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  RotateCw,
  FileText,
  Sliders,
  BookOpen,
  Bell,
} from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { isPlatformAdminRole } from "@/lib/auth/roles";
import { availableHostingFeatures } from "@/lib/hosting/features";
import { BRANDING } from "@/lib/branding";
import { useLanguage } from "@/lib/i18n/context";

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
  badge?: string;
  badgeColor?: string;
}

interface NavSection {
  title: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
  items: NavItem[];
}

export function Sidebar({
  className = "",
  onNavigate,
}: {
  className?: string;
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const { user: authUser, activeAccountId, capabilities, permissions, switchAccount, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const memberships = authUser?.memberships || [];
  const accountName =
    memberships.find((m) => m.accountId === (activeAccountId || memberships[0]?.accountId))?.accountName ||
    memberships[0]?.accountName ||
    null;
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // Collapsible section state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setAccountSwitcherOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSwitchAccount = async (accountId: string) => {
    if (accountId === activeAccountId) {
      setAccountSwitcherOpen(false);
      return;
    }
    setSwitching(true);
    const result = await switchAccount(accountId);
    setSwitching(false);
    setAccountSwitcherOpen(false);
    if (result.success) {
      window.location.reload();
    }
  };

  const isSuperAdmin = !authLoading && isPlatformAdminRole(authUser?.role);

  // Provider Console Structure (organized around admin / DevOps goals)
  const adminNavSections: NavSection[] = [
    {
      title: t("layout.nav.sections.overview"),
      items: [{ title: t("layout.nav.items.dashboard"), href: "/", icon: LayoutDashboard }],
    },
    {
      title: t("layout.nav.sections.websites"),
      items: [
        { title: t("layout.nav.items.websites"), href: "/sites", icon: Globe },
        { title: t("layout.nav.items.domains"), href: "/domains", icon: Globe },
        { title: t("layout.nav.items.dnsZones"), href: "/dns", icon: Network },
        { title: t("layout.nav.items.migrations"), href: "/migrations", icon: ArrowLeftRight, badge: "cPanel" },
        { title: t("layout.nav.items.appTemplates"), href: "/templates", icon: Layers },
      ],
    },
    {
      title: t("layout.nav.sections.customers"),
      items: [
        { title: t("layout.nav.items.accounts"), href: "/accounts", icon: Users },
        { title: t("layout.nav.items.members"), href: "/system/members", icon: Users },
        { title: t("layout.nav.items.hostingPackages"), href: "/packages", icon: PackageIcon },
      ],
    },
    {
      title: t("layout.nav.sections.emailDns"),
      items: [
        { title: t("layout.nav.items.email"), href: "/infrastructure/mail", icon: Mail },
        { title: t("layout.nav.items.dns"), href: "/infrastructure/dns", icon: Network },
        { title: t("layout.nav.items.cloudflare"), href: "/infrastructure/cloudflare", icon: Cloud },
      ],
    },
    {
      title: t("layout.nav.sections.dataBackups"),
      items: [
        { title: t("layout.nav.items.databases"), href: "/infrastructure/databases", icon: Database },
        { title: t("layout.nav.items.cacheRedis"), href: "/infrastructure/cache", icon: Layers },
        { title: t("layout.nav.items.storageVolumes"), href: "/infrastructure/storage", icon: HardDrive },
        { title: t("storage.usage.title"), href: "/storage/usage", icon: HardDrive },
        { title: t("layout.nav.items.backupRestore"), href: "/infrastructure/backup", icon: HardDrive, badge: "S3/Drive" },
        { title: t("layout.nav.items.backupJobs"), href: "/infrastructure/backup/jobs", icon: Clock },
      ],
    },
    {
      title: t("layout.nav.sections.security"),
      items: [
        { title: t("layout.nav.items.securityCenter"), href: "/security", icon: KeyRound },
        { title: t("security.crowdsec.title"), href: "/security/crowdsec", icon: ShieldCheck },
        { title: t("layout.nav.items.securityReports"), href: "/security/reports", icon: FileText },
        { title: t("layout.nav.items.scannerConfig"), href: "/security/config", icon: Sliders },
        { title: t("layout.nav.items.securityEvents"), href: "/security/events", icon: ShieldAlert },
        { title: t("layout.nav.items.auditTrail"), href: "/audit", icon: ShieldCheck },
      ],
    },
    {
      title: t("layout.nav.sections.platform"),
      items: [
        { title: t("layout.nav.items.clusters"), href: "/clusters", icon: Server },
        { title: t("layout.nav.items.buildCenter"), href: "/builds", icon: Hammer },
        { title: t("layout.nav.items.containerRegistry"), href: "/harbor", icon: Box },
        { title: t("layout.nav.items.services"), href: "/infrastructure/services", icon: Boxes },
      ],
    },
    {
      title: t("layout.nav.sections.systemAdvanced"),
      collapsible: true,
      items: [
        { title: t("layout.nav.items.sftpGateway"), href: "/infrastructure/sftp-gateway", icon: KeyRound },
        { title: t("layout.nav.items.reconciliation"), href: "/reconciliation", icon: RotateCw },
        { title: t("layout.nav.items.liveLogs"), href: "/logs", icon: Terminal },
        { title: t("layout.nav.items.debugConsole"), href: "/infrastructure/debug", icon: Bug, badge: "Live" },
        { title: t("layout.nav.items.containerShell"), href: "/infrastructure/shell", icon: Terminal },
        { title: t("layout.nav.items.wikiDoc"), href: "/wiki", icon: BookOpen, badge: "Docs" },
        { title: t("layout.nav.items.systemImages"), href: "/system/images", icon: Box },
        { title: t("layout.nav.items.notifications"), href: "/settings/notifications", icon: Bell },
        { title: t("layout.nav.items.aiProviders"), href: "/settings/ai", icon: Sparkles },
        { title: t("layout.nav.items.settingsVault"), href: "/settings", icon: Settings },
      ],
    },
  ];

  const featureIcons: Record<string, React.ElementType> = { sites: Globe, files: FolderOpen, mail: Mail, dns: Network, cloudflare: Cloud, databases: Database, security: KeyRound, notifications: Bell, backups: HardDrive, protection: ShieldCheck, performance: RotateCw, team: Users, activity: Clock, storage: HardDrive };
  const portalNavSections: NavSection[] = [
    { title: t("layout.nav.sections.portal"), items: [{ title: t("layout.nav.items.dashboard"), href: "/portal", icon: LayoutDashboard }] },
    { title: t("layout.nav.sections.hostingServices"), items: availableHostingFeatures(permissions, capabilities).map((feature) => ({ title: t(feature.titleKey), href: feature.href, icon: featureIcons[feature.id] })) },
  ];

  const navSections = isSuperAdmin ? adminNavSections : portalNavSections;
  const activeHref = navSections
    .flatMap((section) => section.items)
    .filter((item) =>
      item.href === "/" ? pathname === "/" : pathname === item.href || pathname.startsWith(`${item.href}/`)
    )
    .sort((first, second) => second.href.length - first.href.length)[0]?.href;

  const handleNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    onNavigate?.();
  };

  return (
    <aside className={`w-64 bg-surface-sidebar border-r border-themed-border flex flex-col shrink-0 h-screen sticky top-0 select-none z-20 shadow-xl transition-colors ${className}`}>
      {/* Brand Header */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-themed-borderSubtle bg-surface-muted/30">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[var(--accent-primary)] to-[var(--accent-secondary)] flex items-center justify-center text-[var(--accent-text)] font-black shadow-md shadow-[var(--accent-glow)] shrink-0">
          <Flame className="w-5 h-5 text-[var(--accent-text)] stroke-[2.5]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <h1 className="font-extrabold text-xs tracking-wider text-themed-textPrimary uppercase truncate">
              {isSuperAdmin ? BRANDING.productName : t("layout.nav.customerPortal")}
            </h1>
            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] font-bold border border-[var(--accent-primary)]/20 font-mono shrink-0">
              v{BRANDING.version}
            </span>
          </div>
          {memberships.length > 1 ? (
            <div className="relative mt-0.5" ref={switcherRef}>
              <button
                onClick={() => setAccountSwitcherOpen(!accountSwitcherOpen)}
                disabled={switching}
                className="flex items-center gap-1 text-xs text-[var(--accent-primary)] font-medium hover:opacity-80 transition-colors disabled:opacity-50 max-w-[140px]"
              >
                <span className="truncate">{accountName || t("layout.nav.selectAccount")}</span>
                <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${accountSwitcherOpen ? "rotate-180" : ""}`} />
              </button>
              {accountSwitcherOpen && (
                <div className="absolute left-0 top-full mt-1.5 w-56 bg-surface-elevated border border-themed-border rounded-xl shadow-2xl z-50 py-1.5 animate-fade-in">
                  <div className="px-3 py-1 text-[10px] uppercase font-bold text-themed-textMuted tracking-wider">
                    {t("layout.nav.switchAccount")}
                  </div>
                  {memberships.map((m) => (
                    <button
                      key={m.accountId}
                      onClick={() => handleSwitchAccount(m.accountId)}
                      disabled={switching}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors text-left ${
                        m.accountId === activeAccountId
                          ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] font-semibold"
                          : "text-themed-textSecondary hover:bg-surface-cardHover hover:text-themed-textPrimary"
                      }`}
                    >
                      <Check className={`w-3.5 h-3.5 shrink-0 ${m.accountId === activeAccountId ? "text-[var(--accent-primary)]" : "invisible"}`} />
                      <span className="truncate">{m.accountName}</span>
                      <span className="ml-auto text-[10px] text-themed-textMuted font-mono shrink-0">{m.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-themed-textMuted font-medium truncate mt-0.5">
              {isSuperAdmin ? t("layout.nav.providerConsole") : accountName || t("layout.nav.customerPortal")}
            </p>
          )}
        </div>
      </div>

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
        {navSections.map((section) => {
          const isCollapsed = section.collapsible && collapsedSections[section.title];
          return (
            <div key={section.title} className="space-y-1">
              <div
                className={`px-3 py-1 flex items-center justify-between text-[11px] font-bold text-themed-textMuted tracking-wider uppercase ${
                  section.collapsible ? "cursor-pointer hover:text-themed-textPrimary" : ""
                }`}
                onClick={() => section.collapsible && toggleSection(section.title)}
              >
                <span>{section.title}</span>
                {section.collapsible && (
                  <span className="text-themed-textMuted hover:text-themed-textPrimary">
                    {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </span>
                )}
              </div>

              {!isCollapsed && (
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.href === activeHref;

                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={handleNavigation}
                        aria-current={isActive ? "page" : undefined}
                        className={`group relative flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium transition-all duration-150 ${
                          isActive
                            ? "bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] border border-[var(--accent-primary)]/30 font-semibold shadow-sm"
                            : "text-themed-textSecondary hover:text-themed-textPrimary hover:bg-surface-cardHover"
                        }`}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-2 bottom-2 w-1 bg-[var(--accent-primary)] rounded-r shadow-[var(--accent-glow)]" />
                        )}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Icon
                            className={`w-4 h-4 shrink-0 transition-colors ${
                              isActive
                                ? "text-[var(--accent-primary)]"
                                : "text-themed-textMuted group-hover:text-themed-textPrimary"
                            }`}
                          />
                          <span className="truncate">{item.title}</span>
                        </div>

                        {item.badge && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${
                              isActive
                                ? "bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] border border-[var(--accent-primary)]/40"
                                : "bg-surface-muted text-themed-textMuted border border-themed-border"
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Host Environment Indicator */}
      <div className="p-3.5 border-t border-themed-borderSubtle bg-surface-muted/30 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-themed-textSecondary">
          <span className="w-2 h-2 rounded-full bg-status-success shadow-glowEmerald animate-pulse" />
          <span className="font-mono text-themed-textPrimary font-semibold">{isSuperAdmin ? t("layout.nav.providerAdmin") : t("layout.nav.customerAccount")}</span>
        </div>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-status-success-bg border border-status-success-border text-status-success-text font-mono font-bold">
          {t("layout.nav.online")}
        </span>
      </div>
    </aside>
  );
}
